import { useAppStore } from '../stores';
import { UserRole } from '../domain';

export function useAuth() {
  const {
    user,
    roles,
    loading,
    error,
    redirectError,
    mode,
    accountRefused,
    localDataOffer,
    signUp,
    signIn,
    signInWithOAuth,
    signOut: storeSignOut,
    hasRole,
    clearError,
    setRedirectError,
    clearAccountRefused,
    addLocalDataToAccount,
    keepLocalDataOnPhone,
    deleteLocalData,
  } = useAppStore(state => state.auth);
  const unlocks = useAppStore(state => state.licence.unlocks);
  const redeemCode = useAppStore(state => state.licence.redeem);

  return {
    // State
    user,
    roles,
    loading,
    error,
    redirectError,
    /** Someone is using the app, with an account or in local mode: what the app's pages need. */
    isAuthenticated: !!user,
    /** Used without an account, kept on this phone alone (docs/plans/local-mode.md). */
    isLocal: mode === 'local',
    /** Signed into an account. */
    isAccount: mode === 'account',
    /** The last sign-in would have created an account without a code, and was turned away. */
    accountRefused,
    /** A code redeemed on this phone lets a sign-in create an account; undefined while the codes
     * kept are checked. Signing into an account that exists needs none. */
    canCreateAccount: unlocks === undefined ? undefined : unlocks.includes('account'),
    /** What was made in local mode on this phone, to ask the account just signed into whether to
     * add; null when there's nothing to ask. */
    localDataOffer,

    // Methods
    signUp,
    signIn,
    signInWithOAuth,
    signOut: storeSignOut,
    clearError,
    setRedirectError,
    clearAccountRefused,
    /** Redeems a code: true if it held a licence, and it's kept on this phone. */
    redeemCode,
    /** Adds the local data to the account and pushes it; false if that failed. */
    addLocalDataToAccount,
    /** Leaves it on the phone for now: back on signing out, offered again next sign-in. */
    keepLocalDataOnPhone,
    /** Deletes it from the phone, for good; false if that failed. */
    deleteLocalData,

    // Role checks
    hasRole,
    hasPermission: (requiredRoles: UserRole | UserRole[]) => hasRole(requiredRoles),

    // Helpers
    isAdmin: roles.includes(UserRole.Admin),
    isViewer: !roles.includes(UserRole.Admin) && !roles.includes(UserRole.User),
  };
}
